import { useMemo } from 'react';
import { mapImageConstants } from '../utils/imageMapper';
import aiAgentData from '../data/aiAgentData.json';

export interface ChatStep {
  type: string;
  index?: number;
  delay: number;
  value?: number;
}

export interface ChatMessage {
  role: string;
  text: string;
}

export interface ChatAnimationData {
  chatSteps: ChatStep[];
  conversation: ChatMessage[];
}

export interface FunnelStep {
  label: string;
  pct: number;
  color: string;
  factor: number;
}

export interface VisitorInfo {
  color: string;
  delay: number;
}

export interface MissedVisitorAnimationData {
  title: string;
  badge: string;
  statLabel: string;
  visitorCounts: number[];
  funnelSteps: FunnelStep[];
  visitors: VisitorInfo[];
  leftCounts: number[];
}

export interface HeroSectionData {
  badgeText: string;
  titlePart1: string;
  titlePart2: string;
  description: string;
  buttonText: string;
  bullets: string[];
}

export interface LossItem {
  iconName: string;
  label: string;
}

export interface ProblemSectionData {
  title: string;
  subtitle: string;
  losses: LossItem[];
  footerText: string;
}

export interface FeatureItem {
  iconName: string;
  title: string;
  desc: string;
}

export interface FeaturesSectionData {
  badgeText: string;
  title: string;
  description: string;
  features: FeatureItem[];
  tailoredTitle: string;
  tailoredDescription: string;
}

export interface StepItem {
  num: string;
  title: string;
  desc: string;
  iconName: string;
}

export interface HowItWorksSectionData {
  badgeText: string;
  title: string;
  steps: StepItem[];
  footerText: string;
}

export interface ComparisonSectionData {
  badgeText: string;
  title: string;
  description: string;
  headers: string[];
  rows: string[][];
}

export interface CustomizationSectionData {
  badgeText: string;
  title: string;
  description: string;
  items: string[];
  footerText: string;
}

export interface CapabilityItem {
  iconName: string;
  title: string;
  desc: string;
}

export interface WhyNustartzSectionData {
  badgeText: string;
  title: string;
  description: string;
  capabilities: CapabilityItem[];
}

export interface OutcomeItem {
  iconName: string;
  title: string;
  desc: string;
}

export interface OutcomesSectionData {
  badgeText: string;
  title: string;
  outcomes: OutcomeItem[];
}

export interface UseCaseItem {
  iconName: string;
  title: string;
  tagline: string;
  desc: string;
  bullets: string[];
  color: string;
}

export interface IndustryItem {
  name: string;
  desc: string;
}

export interface UseCasesSectionData {
  badgeText: string;
  title: string;
  useCases: UseCaseItem[];
  industriesTitle: string;
  industriesSubtitle: string;
  industries: IndustryItem[];
}

export interface FAQItem {
  q: string;
  a: string;
}

export interface FAQSectionData {
  badgeText: string;
  title: string;
  faqs: FAQItem[];
}

export interface BulletCTA {
  iconName: string;
  label: string;
}

export interface FinalCTAData {
  titlePart1: string;
  titlePart2: string;
  description: string;
  buttonText: string;
  bullets: BulletCTA[];
}

export interface AIAgentData {
  chatAnimation: ChatAnimationData;
  missedVisitorAnimation: MissedVisitorAnimationData;
  heroSection: HeroSectionData;
  problemSection: ProblemSectionData;
  featuresSection: FeaturesSectionData;
  howItWorksSection: HowItWorksSectionData;
  comparisonSection: ComparisonSectionData;
  customizationSection: CustomizationSectionData;
  whyNustartzSection: WhyNustartzSectionData;
  outcomesSection: OutcomesSectionData;
  useCasesSection: UseCasesSectionData;
  faqSection: FAQSectionData;
  finalCTA: FinalCTAData;
}

export function useAIAgentData() {
  const data = useMemo(() => mapImageConstants(aiAgentData as AIAgentData), []);
  const loading = false;
  const error = null;

  return { data, loading, error };
}
